import { flushWindowToRenderer } from '@src/editor/lib/flushWindowToRenderer';
import { SceneService } from '@src/services/SceneService';
import { KaraokeStore } from '@src/stores-v2/KaraokeStore';
import SceneLayoutIndexStore from '@src/stores-v2/SceneLayoutIndexStore';
import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, observable, runInAction } from 'mobx';

import {
  clampToLastFrame,
  collectWindowElements,
  DEFAULT_WINDOWING_CONFIG,
  windowCoversTime,
} from './lib/TimelineWindowFilter';
import { VideoElement } from './models';
import RendererSyncService, {
  resolveResetProps,
  resolveResetPropsMap,
} from './services/RendererSyncService';
import { SceneInitializationService } from './services/SceneInitializationService';
import { migrateBasicTemplateTextPositions } from './services/SceneTextPositionMigration';
import { EditorChangeType } from './types';
import { VideoEditor } from './VideoEditor';

import type { IVideoEditor } from './interface/IVideoEditor';
import type {
  PlaybackCallbacks,
  WindowBounds,
  WindowingConfig,
} from './lib/TimelineWindowFilter';
import type { Scene } from './models/layout/Scene';
import type {
  IScene,
  LayoutOptions,
  LayoutStrategy,
} from './models/layout/types';
import type { Timeline, TimelineJSON } from './models/Timeline';
import type { KaraokeData, KaraokeLine } from './types/karaoke';
import type { RendererState } from '@src/renderer/RendererState';
import type { VideoSource } from '@src/stores-v2';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import type { Video } from '@src/types.ts/story';
import type { TranscriptElementLegacy } from '@src/types.ts/transcript';

const logger = LogManager.getLogger('ClipPreviewEditorStore');

/**
 * ClipPreviewEditorStore is a simplified MobX wrapper around VideoEditor
 * for clip preview rendering. It supports transcript and karaoke data loading
 * but operates in read-only mode for preview purposes.
 *
 * Based on QuoteCardsEditorStore but with added support for transcript/karaoke.
 */
export class ClipPreviewEditorStore {
  // Core state
  editor: IVideoEditor;
  timeline: Timeline;
  isReady: boolean;

  // Headless integration control
  private isLocalChange = false;
  // Flag to suppress refresh during bulk loading operations
  private isBulkLoading = false;

  // Transcript elements — explicit observable field because `editor` is not MobX-tracked.
  // Updated after loadTranscript / loadProjectForPreview so MobX observers re-render.
  //
  // Annotated `observable.ref` in the constructor (PLAT-11150 F2): the array is
  // only ever replaced wholesale by `refreshTranscriptElements`, so reference
  // reactivity is all any consumer needs — and it runs on every timeline change,
  // where deep observability meant MobX walked and re-proxied thousands of
  // element objects each time (~1s of a single section toggle).
  private _transcriptElements: ReturnType<IVideoEditor['getFullTranscript']> =
    [];

  // PLAT-10788 (DS5) — the clip preview owns its own per-instance KaraokeStore
  // (multiple clip previews can be open at once, each on a different clip, so
  // the global singleton can't represent them). Synced from the clip editor in
  // `refreshKaraokeState`; backs the karaoke proxy + the per-language save/switch.
  private readonly _karaokeStore = new KaraokeStore();

  // Selection state
  selectedElementIds: string[] = [];

  // External sync service for iframe interactions
  private rendererExternalSyncService: RendererSyncService | null = null;
  private externalChangeCount = 0;

  // Layout infrastructure for reframing support
  readonly sceneLayoutIndexStore: SceneLayoutIndexStore;

  private videoSourceStore: VideoSourceStore;

  // Layout strategy configuration (set from feature flags)
  private _layoutStrategy: LayoutStrategy = 'hybrid';
  private _sceneCountThreshold: number = 100;
  private _lockSceneBoundElements: boolean = false;

  /**
   * Cached split-window from the last `toPreviewJSON` call.
   * Used by hybrid-adaptive to decide whether a playhead movement
   * crosses a range boundary and requires a source refresh.
   */
  private _splitWindow?: { startTime: number; endTime: number };

  /** Cached windowing bounds from the last collectWindowElements call. */
  private _windowBounds?: WindowBounds;

  /** Timeline windowing configuration (set from feature flags). */
  private _windowingConfig: WindowingConfig = DEFAULT_WINDOWING_CONFIG;

  /** Playback state callbacks wired by the host adapter. */
  private _playbackCallbacks?: PlaybackCallbacks;

  constructor(videoSourceStore: VideoSourceStore) {
    this.videoSourceStore = videoSourceStore;
    this.editor = new VideoEditor();
    this.timeline = this.editor.timeline;
    this.sceneLayoutIndexStore = new SceneLayoutIndexStore();

    this.isReady = runInAction(() => false);

    this.initializeEditor();
    this.initializeRendererSyncService();

    makeAutoObservable(this, {
      editor: false,
      _karaokeStore: false,
      _transcriptElements: observable.ref,
    } as any);
  }

  /**
   * Initializes the editor with observers
   */
  initializeEditor(): void {
    // Register MobX reactivity observer
    this.setupEditorObservers();
  }

  /**
   * Initialize renderer sync service for accepting external changes from iframe
   * and for applying reframing updates from the reframe modal.
   */
  private initializeRendererSyncService(): void {
    this.rendererExternalSyncService = new RendererSyncService({
      layoutIndexStore: this.sceneLayoutIndexStore,
      getScene: (sceneId: string): Scene | undefined => {
        return this.editor.timeline.findSceneById(sceneId);
      },
      updateScene: (sceneId: string, updates: Partial<IScene>): boolean => {
        try {
          return this.acceptExternalChanges(() => {
            return (this.editor as VideoEditor).commandUpdateScene(
              sceneId,
              updates,
            );
          });
        } catch (e) {
          logger.error('Error updating scene in ClipPreview RendererSync', e);
          return false;
        }
      },
      updateScenes: updates => {
        try {
          this.acceptExternalChanges(() => {
            (this.editor as VideoEditor).commandUpdateScenes(updates);
          });
        } catch (e) {
          logger.error(
            'Error updating multiple scenes in ClipPreview RendererSync',
            e,
          );
        }
      },
      updateElement: (elementId, updates) => {
        try {
          return this.acceptExternalChanges(() => {
            return this.editor.commandUpdateElement(
              elementId,
              resolveResetProps(this.editor.getElementById(elementId), updates),
            );
          });
        } catch (e) {
          logger.error('Error updating element in ClipPreview RendererSync', e);
          return false;
        }
      },
      updateElements: updates => {
        try {
          this.acceptExternalChanges(() => {
            this.editor.commandUpdateMultipleElements(
              resolveResetPropsMap(updates, id =>
                this.editor.getElementById(id),
              ),
            );
          });
        } catch (e) {
          logger.error(
            'Error updating multiple elements in ClipPreview RendererSync',
            e,
          );
        }
      },
      getCurrentTime: () => this._playbackCallbacks?.getCurrentTime() ?? 0,
    });
  }

  /**
   * Setup observers to ensure MobX reactivity for editor changes
   */
  private setupEditorObservers(): void {
    const mobxReactivityObserver = {
      onEditorChange: (editor: VideoEditor, changeType: EditorChangeType) => {
        logger.debug('OnEditorChange', changeType);
        if (
          changeType === EditorChangeType.TIMELINE_CHANGED ||
          changeType === EditorChangeType.ELEMENT_ADDED ||
          changeType === EditorChangeType.ELEMENT_REMOVED ||
          changeType === EditorChangeType.ELEMENT_UPDATED ||
          changeType === EditorChangeType.PROJECT_LOADED ||
          changeType === EditorChangeType.COMMAND_UNDONE ||
          changeType === EditorChangeType.COMMAND_REDONE
        ) {
          // Skip refresh during bulk loading or local changes
          if (this.isLocalChange || this.isBulkLoading) return;
          try {
            this.isLocalChange = true;
            this.refreshTimeline();
            this.refreshTranscriptElements();
            this.refreshKaraokeState();
          } finally {
            this.isLocalChange = false;
          }
        }

        // Handle transcript-specific changes (word replace, delete, etc.)
        if (
          changeType === EditorChangeType.TRANSCRIPT_MODIFIED ||
          changeType === EditorChangeType.TRANSCRIPT_LOADED
        ) {
          if (this.isLocalChange || this.isBulkLoading) return;
          try {
            this.isLocalChange = true;
            this.refreshTranscriptElements();
          } finally {
            this.isLocalChange = false;
          }
        }

        // Handle forced refresh after external changes batch complete
        if (changeType === EditorChangeType.FORCED_REFRESH) {
          if (this.isLocalChange || this.externalChangeCount === 0) return;
          logger.info(
            `FORCED_REFRESH processing ${this.externalChangeCount} external changes`,
          );
          try {
            this.isLocalChange = true;
            this.refreshTimeline();
            this.refreshKaraokeState();
            this.externalChangeCount = 0;
          } finally {
            this.isLocalChange = false;
          }
          return;
        }
      },
    };

    this.editor.registerObserver(mobxReactivityObserver);
  }

  /**
   * Configure the layout strategy used by `refreshTimeline` (preview path).
   * Called from the stores layer when LaunchDarkly flags change.
   */
  setPreviewLayoutConfig(config: {
    strategy?: LayoutStrategy;
    sceneCountThreshold?: number;
    lockSceneBoundElements?: boolean;
  }): void {
    if (config.strategy !== undefined) this._layoutStrategy = config.strategy;
    if (config.sceneCountThreshold !== undefined)
      this._sceneCountThreshold = config.sceneCountThreshold;
    if (config.lockSceneBoundElements !== undefined)
      this._lockSceneBoundElements = config.lockSceneBoundElements;
  }

  getPreviewLayoutConfig(): {
    strategy: LayoutStrategy;
    sceneCountThreshold: number;
  } {
    return {
      strategy: this._layoutStrategy,
      sceneCountThreshold: this._sceneCountThreshold,
    };
  }

  /**
   * Configure the timeline windowing filter used by `refreshTimeline`.
   * Called from the stores layer when LaunchDarkly flags change.
   */
  setWindowingConfig(config: WindowingConfig): void {
    this._windowingConfig = config;
    logger.info('[setWindowingConfig] updated', this._windowingConfig);
  }

  get isWindowingActive(): boolean {
    return this._windowingConfig.enabled;
  }

  /**
   * Returns `true` when hybrid-adaptive is active and the given time falls
   * outside the cached split-range window — meaning a `refreshTimeline()`
   * call is needed to recalculate the layout around the new playback position.
   */
  needsHybridAdaptiveRefresh(currentTime: number): boolean {
    if (this._layoutStrategy !== 'hybrid-adaptive') return false;
    if (!this._splitWindow) return true; // never computed yet
    return (
      currentTime < this._splitWindow.startTime ||
      currentTime > this._splitWindow.endTime
    );
  }

  /**
   * Force refresh the video source to trigger MobX reactivity
   * This is a critical method for ensuring the UI updates when the timeline changes
   */
  refreshTimeline(atTime?: number): void {
    const reorderOpts = {
      reorderLayoutCompositionTrackToBottom: this.editor.isFlagOn(
        'reorderLayoutCompositionTrackToBottom',
      ),
    };

    const layoutOptions: LayoutOptions = {
      strategy: this._layoutStrategy,
      sceneCount: this.editor.timeline.scenes.length,
      sceneCountThreshold: this._sceneCountThreshold,
      currentTime: atTime ?? this._playbackCallbacks?.getCurrentTime(),
      lockSceneBoundElements: this._lockSceneBoundElements,
    };

    const previewResult = this.editor.timeline.toPreviewJSON(
      layoutOptions,
      false,
      reorderOpts,
    );

    // Cache the split-window for hybrid-adaptive playhead tracking
    this._splitWindow = previewResult.splitWindow;
    let { tempEnhancements, ...newSource } = previewResult.json;

    if (this.isWindowingActive && newSource.elements) {
      const windowingResult = this.applyWindowingFilter(newSource, atTime);
      newSource = windowingResult.source;
      this._windowBounds = windowingResult.windowBounds;
      logger.debug('[refreshTimeline] windowed source', newSource);
    }
    // Update scene layout index from the layout representation map
    // produced by LayoutEngine during toPreviewJSON()
    this.sceneLayoutIndexStore.updateFromLayoutRepresentationMap(
      tempEnhancements?.layoutRepresentationMap ?? null,
    );

    this.editor.timeline.reapplyCoupledElementBounds(
      tempEnhancements?.coupledElementBounds,
    );

    // Preserve fonts from the current source
    const currentFonts = this.videoSourceStore.source?.fonts;
    const sourceWithFonts = currentFonts
      ? { ...newSource, fonts: currentFonts }
      : newSource;

    runInAction(() => {
      this.videoSourceStore.setSource(sourceWithFonts);
    });
  }

  /**
   * Accepts external changes from iframe without triggering timeline refresh
   * This prevents circular updates between editor and renderer
   */
  private acceptExternalChanges<T>(action: () => T): T {
    try {
      this.isLocalChange = true;
      this.externalChangeCount++;
      return action();
    } finally {
      this.isLocalChange = false;
    }
  }

  /**
   * Sync external changes from iframe to editor state
   * Only call this for user-driven changes from the iframe preview (drag, resize, etc.)
   */
  syncExternalChanges(
    prevState: RendererState | undefined,
    newState: RendererState,
  ): void {
    if (!this.rendererExternalSyncService) {
      logger.warn('External sync service not initialized');
      return;
    }
    this.rendererExternalSyncService.sync(prevState, newState);
  }

  exportProject() {
    return { sources: this.editor.exportVideoSources() };
  }

  /**
   * Load a complete video project for preview with all resources
   * Mirrors EditorStore.loadProject logic
   */
  loadProjectForPreview(options: {
    videoSource: VideoSource;
    transcript?: TranscriptElementLegacy[];
    originalTranscription?: TranscriptElementLegacy[];
    karaokeData?: KaraokeData;
    // PLAT-10788 — per-language karaoke variants + the language context needed to
    // key/activate them (DS8/DS9). `karaokeData` alone (no map) still works: it's
    // wrapped into a one-entry cache by its own config.language.
    karaokeDataByLanguage?: Record<string, KaraokeData>;
    transcriptLanguage?: string | null;
    originalVideoLanguage?: string | null;
    scenes?: any[]; // IScene[] type from layout
    originalVideoTrackId?: number | null;
    extraElementData?: Video['extraElementData'];
    forcedAlignment?: boolean;
  }): void {
    const {
      videoSource,
      transcript,
      originalTranscription,
      karaokeData,
      karaokeDataByLanguage,
      transcriptLanguage,
      originalVideoLanguage,
      scenes,
      originalVideoTrackId,
      extraElementData,
    } = options;

    // Suppress intermediate refreshes during bulk loading
    this.isBulkLoading = true;

    // 1. Pass extra element data first — loadVideoSource calls
    //    convertMarkedLogosToLogoElements which needs this data.
    //    Always set (even if empty) to clear stale data from prior loads.
    if (!extraElementData) {
      logger.warn(
        'No extraElementData provided to loadProjectForPreview - logos may not render correctly',
      );
    }
    this.editor.passExtraElementData(extraElementData ?? {});

    // 2. Load the video source (creates timeline, converts logos, identifies coupling)
    this.editor.loadVideoSource(videoSource);

    // NEW REMAPPING LOGIC to avoid issues with inconsistent track re-order logic between Timeline.discardLayoutTracks and remapTrackId
    const sourceVideoElement =
      Array.isArray(videoSource.elements) && originalVideoTrackId
        ? videoSource.elements.find(
            el => String(el.track) === String(originalVideoTrackId),
          )
        : null;

    const remappedVideoTrackId = sourceVideoElement
      ? this.editor.timeline.allElements.find(
          el =>
            el instanceof VideoElement &&
            String(el.id) === String(sourceVideoElement.id),
        )?.track
      : null;

    // // Remap originalVideoTrackId if tracks were renumbered during normalization
    // const remappedVideoTrackId =
    //   originalVideoTrackId != null
    //     ? this.editor.timeline.remapTrackId(originalVideoTrackId)
    //     : null;

    // 3. Load scenes after video source but before transcript
    // This ensures scenes are present when transcript elements reference them
    this.editor.loadScenes(
      scenes && scenes.length > 0
        ? scenes
        : SceneInitializationService.ensureScenesInitialized(
            this.editor.timeline,
            remappedVideoTrackId != null
              ? remappedVideoTrackId.toString()
              : undefined,
          ).map(s => s.toJSON()),
    );

    // 4. Load transcript if provided
    // Use originalTranscription as fallback for the second parameter
    if (transcript && transcript.length > 0) {
      const originalTranscript = originalTranscription || transcript;
      this.editor.loadTranscript(
        transcript as TranscriptElementLegacy[],
        originalTranscript as TranscriptElementLegacy[],
        remappedVideoTrackId?.toString() || undefined,
        { forcedAlignment: options.forcedAlignment ?? false },
      );
    }

    // 5. Identify and mark coupled elements (logos, etc.)
    // This is important for layout relationships
    this.editor.getTrackManager().identifyAndMarkCoupledElements();
    this.editor.getTranscriptTrackManager().applyDefaultAudioFades();

    // should be after loading transcript as scenes bound to transcript track
    new SceneService().validateAndAdjustScenes(this.editor.timeline);

    // 6. Load karaoke data last (depends on transcript being loaded).
    // PLAT-10788 — hydrate the clip's per-language cache and load the active
    // variant. Loading even an empty `{ clips: [] }` variant seeds the karaoke
    // configs + default config id (gating on a track left the editor
    // config-less, throwing on the next produce).
    const karaokeLoaded = this.hydrateAndLoadKaraoke({
      karaokeData,
      karaokeDataByLanguage,
      transcriptLanguage,
      originalVideoLanguage,
    });
    // NOTE: loading a clip used to RE-PRODUCE its captions whenever
    // `transcript_maximum_length` was set ("so clip boundaries match the
    // Creatomate rendering", PLAT-9265) — a workaround for the renderer
    // re-paging our rows by that same setting. `replaceTrack: true` rebuilds
    // every row from the transcript, so it silently discarded every caption
    // line break the user had adjusted by hand, on every open. The renderer is
    // no longer handed a paging budget (`KaraokeClip.toJSON`), so the persisted
    // rows ARE what renders and re-producing could only destroy work.
    if (!karaokeLoaded) {
      // Initialize default karaoke config even if no data
      this.editor.initializeDefaultKaraokeData();
    }

    // Migration: strip auto-initialized textPosition from basic-template scenes
    // so they correctly inherit from global KaraokeConfig instead of being
    // treated as per-scene overrides.
    const defaultConfig = this.editor.getDefaultKaraokeConfig();
    if (defaultConfig) {
      migrateBasicTemplateTextPositions(
        this.editor.timeline.scenes,
        defaultConfig,
      );
    }

    // End bulk loading - allow refresh now
    this.isBulkLoading = false;

    // 7. Force refresh to render scenes into supporting tracks (90+)
    // This calls timeline.toPreviewJSON() which applies layout segments to scenes
    this.refreshTimeline();

    // 8. Snapshot transcript and karaoke into observable fields for MobX reactivity
    this.refreshTranscriptElements();
    this.refreshKaraokeState();

    runInAction(() => {
      this.isReady = true;
    });
  }

  /**
   * Load a video source into the editor (simple version without transcript/karaoke)
   */
  loadVideoSource(videoSource: VideoSource): void {
    this.editor.loadVideoSource(videoSource);
    runInAction(() => {
      this.isReady = true;
    });
  }

  /**
   * Get current transcript elements from the editor.
   * Used by the clip transcript bridge to feed the Transcript component.
   */
  get transcriptElements() {
    return this._transcriptElements;
  }

  /**
   * Refresh the observable transcript elements from the editor.
   * Must be called after any operation that changes transcript state
   * (loadTranscript, loadProjectForPreview) because `editor` is not MobX-tracked.
   */
  refreshTranscriptElements(): void {
    runInAction(() => {
      this._transcriptElements = this.editor.getFullTranscript();
    });
  }

  /**
   * The clip preview's own per-instance KaraokeStore (PLAT-10788 / DS5). Drives
   * the per-language cache, save, and language switch for this clip.
   */
  get karaokeStore(): KaraokeStore {
    return this._karaokeStore;
  }

  /**
   * Current karaoke lines — delegated to the owned KaraokeStore (kept as a
   * getter so the clipTranscriptProxies karaoke proxy is unchanged).
   */
  get karaokeLines(): KaraokeLine[] {
    return this._karaokeStore.karaokeLines;
  }

  /**
   * PLAT-10788 — hydrate the clip's per-language karaoke cache (DS8/DS9, shared
   * with the main path via `KaraokeStore.hydrateLoadedByLanguage`) and load the
   * active variant into the clip editor. Returns whether a variant was loaded
   * (false ⇒ caller should `initializeDefaultKaraokeData`). Used by
   * `loadProjectForPreview` and the late-load fallback in `useClipPreviewRenderer`.
   */
  hydrateAndLoadKaraoke(args: {
    karaokeData?: KaraokeData | null;
    karaokeDataByLanguage?: Record<string, KaraokeData>;
    transcriptLanguage?: string | null;
    originalVideoLanguage?: string | null;
  }): boolean {
    const active = this._karaokeStore.hydrateLoadedByLanguage(args);
    if (active) {
      // Reconcile like every other karaoke load (language switch, publish, any
      // in-session command): persisted rows only map the token ids they hold,
      // so a transcript word covered by no row would render uncaptioned. A
      // no-op when the variant already covers the transcript, and unlike the
      // load-time re-produce this replaces, it keeps manual line breaks.
      this.editor.loadKaraokeData(active, { reconcileAgainstTranscript: true });
      return true;
    }
    return false;
  }

  /**
   * Refresh karaoke state from the editor. Must be called after karaoke data
   * load and after karaoke commands (add/remove break, hide/show, produce)
   * because `editor` is not MobX-tracked. Syncs the owned KaraokeStore, whose
   * observable `karaokeLines` drives the proxy's observers.
   */
  refreshKaraokeState(): void {
    this._karaokeStore.syncFromEditor(this.editor);
  }

  /**
   * Get the renderer sync service for applying reframing updates.
   */
  get rendererSyncService(): RendererSyncService | undefined {
    return this.rendererExternalSyncService ?? undefined;
  }

  /**
   * Returns `true` when timeline windowing is active and the playhead is
   * approaching or has moved outside the current window bounds.
   */
  needsWindowingRefresh(currentTime: number): boolean {
    if (!this.isWindowingActive) return false;
    if (!this._windowBounds) return true;
    const { recheckThreshold } = this._windowingConfig.playback;
    return (
      currentTime >= this._windowBounds.timeB - recheckThreshold ||
      currentTime < this._windowBounds.timeA ||
      currentTime > this._windowBounds.timeB
    );
  }

  /**
   * Guarantee the renderer holds `time` before it is asked to present it.
   *
   * Windowing sends the renderer only the elements around the playhead, and it
   * recomputes that window on every layout refresh — around whatever position
   * the playhead held at that moment. So a refresh triggered by a structural
   * edit (a reel reorder) or by playback leaves the renderer holding a source
   * that begins wherever the playhead happened to be. Seeking or playing into a
   * position the window excludes then asks the renderer to present footage it
   * was never sent, and it shows black.
   *
   * The recovery already existed — `needsWindowingRefresh` turns true once the
   * playhead falls outside the bounds — but it *trails* the command by a 300 ms
   * debounce (and is skipped entirely during playback), so the renderer starts
   * on content it does not have and the refresh chases it. Leading the command
   * instead of trailing it is what removes the black: rebuild the window around
   * the target and flush it, then let the caller seek or play.
   *
   * Callers on the scrub path must skip this — a scrub issues seeks
   * continuously, and the scrubbing window has its own debounced reaction
   * (`AdaptivePlaybackSourceManager`) for exactly that reason.
   */
  async ensureWindowCovers(time: number): Promise<void> {
    if (this.windowCovers(time)) return;
    this.refreshTimeline(time);
    await flushWindowToRenderer(this.videoSourceStore);
  }

  /**
   * Whether the renderer already holds `time`.
   *
   * Windowing off means it holds everything. Otherwise this is plain
   * containment — see `windowCoversTime` for why it must not be
   * `needsWindowingRefresh`.
   */
  private windowCovers(time: number): boolean {
    if (!this.isWindowingActive) return true;
    return windowCoversTime(this._windowBounds, time);
  }

  /**
   * Apply the timeline windowing filter to the serialized source,
   * replacing its elements with only those in the visible window.
   *
   * `atTime` overrides the playhead the window is centred on, so a seek can
   * build the window around where it is going rather than where it is.
   */
  private applyWindowingFilter(
    source: TimelineJSON,
    atTime?: number,
  ): {
    source: TimelineJSON;
    windowBounds: WindowBounds;
  } {
    const currentTime = clampToLastFrame(
      atTime ?? this._playbackCallbacks?.getCurrentTime() ?? 0,
      source,
    );
    const isScrubbing = this._playbackCallbacks?.getIsScrubbing() ?? false;
    const { visibleElements, timeA, timeB } = collectWindowElements(
      source.elements as Record<string, any>[],
      currentTime,
      isScrubbing,
      this._windowingConfig.playback,
      this._windowingConfig.scrubbing,
    );

    source.elements = visibleElements as any;
    return { source, windowBounds: { timeA, timeB } };
  }

  /**
   * Wire playback state callbacks so the store can resolve the current
   * playhead position and playing state for windowing, layout, and sync.
   * Must be called after the renderer is ready and time tracking is set up.
   */
  setPlaybackCallbacks(callbacks: PlaybackCallbacks): void {
    this._playbackCallbacks = callbacks;
    logger.info('playback callbacks set for clip preview editor');
  }

  /**
   * Select an element in the timeline (no-op for clip preview)
   * This method is required by EditorStoreLike interface
   */
  selectElement(id: string): void {
    this.selectedElementIds = [id];
    // This is a no-op implementation to satisfy the interface
  }

  /**
   * Bracket an async operation that performs many editor mutations so that
   * observer notifications (refreshTimeline, refreshTranscriptElements,
   * refreshKaraokeState) are suppressed until the entire operation completes.
   *
   * Uses the same `isBulkLoading` flag that `loadProjectForPreview` relies on,
   * so the observer at `onEditorChange` skips intermediate refreshes.
   * A single refresh is performed in `finally` to reconcile MobX state.
   */
  async withBulkOperation<T>(operation: () => Promise<T>): Promise<T> {
    this.isBulkLoading = true;
    try {
      return await operation();
    } finally {
      this.isBulkLoading = false;
      this.refreshTimeline();
      this.refreshTranscriptElements();
      this.refreshKaraokeState();
    }
  }

  /**
   * Cleanup method to dispose of the store
   */
  dispose(): void {
    // Clean up any subscriptions or references
    this.rendererExternalSyncService = null;
    this.sceneLayoutIndexStore.dispose();
  }
}
